import { ReactElement, useMemo, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { AddRounded } from '@mui/icons-material';
import LocationForm from '@/features/locations/LocationForm';
import { Level, Site } from '@/core/entities';
import useAuth from '@/shared/context/auth-context';
import { Roles } from '@/core/types';
import DropdownButton from '@/components/ui/dropdown-button';
import { downloadTemplate } from '@/api/sites';
import { downloadFile } from '@/shared/utils/file';
import UploadLocationsDialog from '@/features/sites/UploadLocationsDialog';

export interface LocationsTableHeaderProps {
	site: Site;
	level: Level;
	parentLocationId?: string;
	controlLocationTemplate?: boolean;
	refetchLocations?: () => Promise<void> | void;
}

const LocationsTableHeader = ({
	site,
	level,
	parentLocationId,
	refetchLocations,
	controlLocationTemplate,
}: LocationsTableHeaderProps): ReactElement => {
	const { user } = useAuth();
	const { name: levelName, siteId } = level;

	const { role } = user || {};
	const canCreateLocation = useMemo(
		() => role && [Roles.LEVIANT_ADMIN, Roles.ACCOUNT_ADMIN, Roles.SITE_ADMIN].includes(role),
		[role]
	);
	const canDownloadAndUploadLocations = useMemo(
		() =>
			controlLocationTemplate &&
			role &&
			[Roles.LEVIANT_ADMIN, Roles.ACCOUNT_ADMIN, Roles.SITE_ADMIN].includes(role),
		[role, controlLocationTemplate]
	);

	const [locationForm, setLocationForm] = useState({ open: false });
	const [locationsUploadDialog, setLocationsUploadDialog] = useState({ open: false });

	const onDownloadTemplate = async () => {
		const data = await downloadTemplate(siteId);
		downloadFile(data, `Locations(${site.name}).csv`);
	};

	return (
		<Stack>
			<Stack direction="row" spacing={2}>
				{canDownloadAndUploadLocations && (
					<DropdownButton
						label="Csv"
						items={[
							{
								label: 'Upload locations CSV',
								onClick: () => setLocationsUploadDialog({ open: true }),
							},
							{
								label: 'Download template CSV',
								onClick: () => {
									/* eslint-disable @typescript-eslint/no-floating-promises */
									onDownloadTemplate();
								},
							},
						]}
					/>
				)}
				{canCreateLocation && (
					<Button
						variant="contained"
						startIcon={<AddRounded />}
						size="large"
						onClick={() => setLocationForm({ open: true })}>
						{`Create ${levelName || ''}`}
					</Button>
				)}
			</Stack>
			{canCreateLocation && (
				<LocationForm
					open={locationForm.open}
					onClose={() => setLocationForm({ ...locationForm, open: false })}
					level={level}
					parentId={parentLocationId}
					siteId={siteId}
					onSubmitted={refetchLocations}
				/>
			)}
			{canDownloadAndUploadLocations && (
				<UploadLocationsDialog
					open={locationsUploadDialog.open}
					onClose={() => setLocationsUploadDialog({ open: false })}
					siteId={siteId}
					onSubmitted={refetchLocations}
				/>
			)}
		</Stack>
	);
};

export default LocationsTableHeader;
